import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, ArrowRight, TrendingUp, Activity } from 'lucide-react';
import { districts, stateData } from '../mock';

const StateView = () => {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    if (status === 'good') return '#10ac84';
    if (status === 'moderate') return '#f39c12';
    return '#e74c3c';
  };

  const getStatusBg = (status) => {
    if (status === 'good') return '#e8f5f1';
    if (status === 'moderate') return '#fff4e6';
    return '#fee';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Bihar State View</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>District-wise Solar Power Generation Overview</p>
      </div>

      {/* State Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" style={{ color: '#10ac84' }} />
            State-Wide Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>Total Districts</div>
              <div className="text-3xl font-bold" style={{ color: '#0a3d62' }}>{districts.length}</div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>Total Sites</div>
              <div className="text-3xl font-bold" style={{ color: '#0a3d62' }}>{stateData.totalSites}</div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>Total Capacity</div>
              <div className="text-3xl font-bold" style={{ color: '#10ac84' }}>{stateData.totalCapacity} MW</div>
            </div>
            <div>
              <div className="text-sm mb-2" style={{ color: '#576574' }}>Today's Generation</div>
              <div className="text-3xl font-bold" style={{ color: '#3498db' }}>{stateData.todayGeneration} kWh</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Districts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {districts.map((district) => (
          <Card
            key={district.id}
            className="hover:shadow-xl transition-all cursor-pointer"
            onClick={() => navigate(`/district/${district.id}`)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" style={{ color: getStatusColor(district.status) }} />
                  {district.name}
                </CardTitle>
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getStatusColor(district.status) }} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div className="text-xs mb-1" style={{ color: '#576574' }}>Sites</div>
                  <div className="text-xl font-bold" style={{ color: '#0a3d62' }}>{district.sites}</div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#f5f7fa' }}>
                  <div className="text-xs mb-1" style={{ color: '#576574' }}>Capacity</div>
                  <div className="text-xl font-bold" style={{ color: '#0a3d62' }}>{district.capacity} MW</div>
                </div>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: getStatusBg(district.status) }}>
                <div className="text-sm mb-2" style={{ color: '#576574' }}>Today's Generation</div>
                <div className="text-2xl font-bold mb-3" style={{ color: getStatusColor(district.status) }}>
                  {district.todayGen} kWh
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: '#576574' }}>PR: <span className="font-semibold">{district.pr}%</span></span>
                  <span style={{ color: '#576574' }}>CUF: <span className="font-semibold">{district.cuf}%</span></span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/district/${district.id}`);
                }}
              >
                View District Details <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Performance Legend */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Status Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#10ac84' }} />
              <div>
                <div className="font-semibold" style={{ color: '#0a3d62' }}>Good</div>
                <div className="text-sm" style={{ color: '#576574' }}>PR ≥ 70%, CUF ≥ 17%</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#f39c12' }} />
              <div>
                <div className="font-semibold" style={{ color: '#0a3d62' }}>Moderate</div>
                <div className="text-sm" style={{ color: '#576574' }}>PR 60-70%, CUF 15-17%</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#e74c3c' }} />
              <div>
                <div className="font-semibold" style={{ color: '#0a3d62' }}>Poor</div>
                <div className="text-sm" style={{ color: '#576574' }}>PR {'<'} 60%, CUF {'<'} 15%</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StateView;
